import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../components/common/Loader";
import Notification from "../../components/common/Notification";
import { getAllEventCategories } from "../../redux/slices/eventCategorySlice";
import { createPressCategory, updatePressCategory } from "../../redux/slices/pressCategorySlice";

const StyledFormContainer = styled(Paper)(({ theme }) => ({
  maxWidth: "800px",
  margin: "20px auto",
  padding: "32px",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
  "& .MuiTextField-root, & .MuiSelect-root": {
    backgroundColor: "#ffffff",
  },
  "& .form-title": {
    borderBottom: "2px solid #f0f0f0",
    paddingBottom: "16px",
    marginBottom: "24px",
  },
}));

const StyledBackButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "#f5f5f5",
  "&:hover": {
    backgroundColor: "#e0e0e0",
  },
  marginRight: "16px",
}));

const Form = ({ initialData = null }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Extract initialData from location state if available
  const dataFromState = location.state?.initialData;
  const formInitialData = dataFromState || initialData;

  const { loading } = useSelector((state) => state.eventCategory);

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    status: "active",
  });

  const [notification, setNotification] = useState({
    show: false,
    type: "success",
    message: "",
  });

  // If initialData is provided, populate form (for update)
  useEffect(() => {
    if (formInitialData) {
      setFormData({
        name: formInitialData.name || "",
        slug: formInitialData.slug || "",
        status: formInitialData.status || "active",
      });
    }
  }, [formInitialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      setFormData((prev) => ({
        ...prev,
        [name]: value,
        slug: slug,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, show: false });
  };

  const handleBack = () => {
    navigate("/press-coverage-category");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let result;
      if (formInitialData) {
        result = await dispatch(
          updatePressCategory({
            id: formInitialData._id, 
            data: formData,
          })
        ).unwrap();
      } else {
        result = await dispatch(createPressCategory(formData)).unwrap();
      }

      // Also update the success message:
      setNotification({
        show: true,
        type: "success",
        message: formInitialData
          ? "Category updated successfully!"
          : "Category created successfully!",
      });

      // And the loader message:
      <Loader
        loading={loading}
        status={formInitialData ? "Updating Category..." : "Creating Category..."}
      />;

      if (result) {
        dispatch(
          getAllEventCategories({
            status: "all",
            page: 1,
            limit: 10,
            sortKey: "createdAt",
            sortOrder: "desc",
          })
        );

        // Use a more reliable navigation approach
        const timer = setTimeout(() => {
          navigate("/press-coverage-category", { replace: true });
        }, 1000);

        return () => clearTimeout(timer);
      }
    } catch (error) {
      console.error("Operation failed:", error);
      setNotification({
        show: true,
        type: "error",
        message: error.message || "Operation failed. Please try again.",
      });
    }
  };

  return (
    <Box sx={{ padding: "20px" }}>
      {loading && (
        <Loader
          loading={loading}
          status={formInitialData ? "Updating Category..." : "Creating Category..."}
        />
      )}

      {notification.show && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={handleCloseNotification}
          autoClose={true}
          duration={3000}
        />
      )}

      <StyledFormContainer elevation={0}>
        <Box
          className="form-title"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <StyledBackButton
            onClick={handleBack}
            size="medium"
            aria-label="back"
          >
            <ArrowBackIcon />
          </StyledBackButton>
          <Typography
            variant="h5"
            className="poppins-font"
            sx={{
              color: "#1a1a1a",
              fontWeight: 600,
              fontSize: "1.2rem",
            }}
          >
            {formInitialData ? "Update Category" : "Create New Category"}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              width: "100%",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            <TextField
              fullWidth
              label="Name *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="poppins-font"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "&:hover fieldset": {
                    borderColor: "#2196f3",
                  },
                },
              }}
            />

            <TextField
              fullWidth
              label="Slug"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              helperText="URL-friendly version of the name (auto-generated)"
              disabled
              className="poppins-font"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#f8f9fa",
                },
              }}
            />

            <FormControl fullWidth required size="small">
              <InputLabel className="poppins-font">Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                label="Status *"
                onChange={handleChange}
                className="poppins-font"
                sx={{
                  borderRadius: "8px",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2196f3",
                  },
                }}
              >
                <MenuItem value="active" className="poppins-font">
                  Active
                </MenuItem>
                <MenuItem value="inactive" className="poppins-font">
                  Inactive
                </MenuItem>
              </Select>
            </FormControl>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
                pt: 3,
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <Button
                onClick={handleBack}
                variant="outlined"
                className="poppins-font"
                disabled={loading}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  padding: "8px 24px",
                  borderColor: "#999",
                  color: "#666",
                  "&:hover": {
                    borderColor: "#666",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                className="poppins-font"
                disabled={loading}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  padding: "8px 24px",
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                }}
              >
                {loading
                  ? "Submitting..."
                  : formInitialData
                  ? "Update"
                  : "Create Category"}
              </Button>
            </Box>
          </Box>
        </form>
      </StyledFormContainer>
    </Box>
  );
};

export default Form;
